import React, { useEffect } from "react";
import BarChart from "./Chart"; // Assuming you have this import for the Chart component
import { ReactSVG } from "react-svg";
import selfSvg from "../../img/self-cropped.svg";
import { RiSparkling2Fill, RiDownload2Fill, RiUpload2Fill } from "react-icons/ri";
import { FaFileCsv, FaFilePdf } from "react-icons/fa";
import {
  Avatar,
  Box,
  Flex,
  HStack,
  Icon,
  Link,
  VStack,
  Text,
  PopoverTrigger,
  PopoverArrow,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  PopoverContent,
  Popover, Portal, PopoverAnchor
} from "@chakra-ui/react";
import {cqAPI} from "../../lib/Auth";

const downloadableBuffers = ['pdf-file', 'csv-file'];

function parseTable(htmlStr, just_verify) {
  // Extract headers using regex
  const headerRegex = /<th>(.*?)<\/th>/g;
  let match;
  const headers = [];
  while ((match = headerRegex.exec(htmlStr))) {
    headers.push(match[1]);
  }

  // Extract rows using regex
  const rowRegex = /<tr>([\s\S]*?)<\/tr>/g; // Using [\s\S] to match across lines
  const rows = [];
  let rowData;
  while ((rowData = rowRegex.exec(htmlStr))) {
    const cells = [];
    const cellRegex = /<td>(.*?)<\/td>/g;
    let cellMatch;
    while ((cellMatch = cellRegex.exec(rowData[1]))) {
      cells.push(cellMatch[1]);
    }
    if (cells.length) {
      rows.push(cells);
    }
  }

  let inValidData = false;

  // Create the resulting structure
  const columnData = [];
  for (let i = 1; i < headers.length; i++) {
    const data = rows.map((row) => {
      const value =
        row[i] && row[i].replace("%", "").replace("$", "").replace(/\,/g, "");
      if (isNaN(String(value))) {
        inValidData = true;
      }
      return [row[0], isNaN(value) ? value : parseFloat(value)];
    });
    columnData.push({ key: headers[i], data: data });
  }
  if (just_verify) {
    return !inValidData;
  }
  return columnData;
}

function MessageList({
  messages,
  chatBoxRef,
  promptSuggestions = [],
  fillQuestion,
  disableSendButton,
  longProcessing,
  uploadCSVFile
}) {
  useEffect(() => {
    if (chatBoxRef.current) {
      const scrollHeight = chatBoxRef.current.scrollHeight;
      chatBoxRef.current.scrollTop = scrollHeight;
    }
  }, [messages, chatBoxRef]);

  const hasCompleteTable = (content) => {
    return (
      content &&
      content.includes("<table") &&
      content.includes("</table>") &&
      parseTable(content, true)
    );
  };
  
  const getFileDescription = (buffer) => {
    switch (buffer.type) {
      case 'pdf-file':
        return 'PDF';
      case 'csv-file':
        return 'CSV';
    }
  }

  const downloadFile = async (attachmentUrl, fileName) => {
    try {
      const downloadUrl = await cqAPI.downloadFile(attachmentUrl);
      if (!downloadUrl) {
        console.error("File unavailable for download:", attachmentUrl);
        return;
      }

      // Create a temporary link element and trigger the download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName); // Set the file name for the download
      document.body.appendChild(link);
      link.click();

      // Clean up by revoking the blob URL and removing the temporary link
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  return (
    <div className="message-list" ref={chatBoxRef}>
      {messages.length === 0 ? (
        <div className="empty-message-list">
          {disableSendButton && (
            <div
              className="spinner"
              style={{ position: "absolute", bottom: "0px" }}
            ></div>
          )}
          {promptSuggestions.length > 0 && (
            <div className="prompt-suggestions">
              {promptSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`prompt-suggestion severity-${suggestion.severity.toLowerCase()}`}
                  onClick={() => fillQuestion(suggestion.prompt)}
                >
                  <div className="prompt-title">{suggestion.title}</div>
                  <div className="prompt-content">{suggestion.prompt}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        messages.map((message, index) => {
          if (message.type === "loading") {
            return (
              <div key={index} className="message server">
                <div className="spinner"></div>
              </div>
            );
          }

          // For other types, display the message content
          return (
            <React.Fragment key={index}>
              <div className={`message ${message.type}`}>
                {message.type === "server" && (
                  <Box mr={4} className="sender-title">
                    <Avatar size="md"
                            bg={""}
                            icon={<Icon as={RiSparkling2Fill} boxSize={9} />}
                    />
                  </Box>
                )}
                {message.type === "user" && (
                  <ReactSVG src={selfSvg} className="sender-title" />
                )}
                <div dangerouslySetInnerHTML={{ __html: message.content }} />
                {hasCompleteTable(message.content) && (
                  <BarChart data={parseTable(message.content)} />
                )}
                {(message?.r_buffers || []).filter(b => downloadableBuffers.includes(b.type)).length > 0 && (
                  <Flex key={`message-${index}`} direction="column">
                    <VStack align="start">
                      {index === ((messages?.length ?? 0) - 1) && (
                        <Box pt="4">
                          <Text as='b'>
                            Download and check the list before continuing with AI patient outreach.
                          </Text>
                          <Text>
                            You can edit and upload the list if needed.
                          </Text>
                        </Box>
                      )}
                      <Box>
                        <Popover placement="bottom-start">
                          <PopoverAnchor>
                            <HStack align="start">
                              <Box>
                                <PopoverTrigger>
                                  <Icon as={RiDownload2Fill} boxSize={4}/>
                                </PopoverTrigger>
                              </Box>
                              {index === ((messages?.length ?? 0) - 1) && (
                                <Box>
                                  <Icon as={RiUpload2Fill} boxSize={4} onClick={() => uploadCSVFile()}/>
                                </Box>
                              )}
                            </HStack>
                          </PopoverAnchor>
                          <Portal>
                            <PopoverContent>
                              <PopoverBody>
                                <VStack w={40}>
                                  {message.r_buffers.filter(b => downloadableBuffers.includes(b.type)).map((buffer, index) => (
                                    <Link
                                      key={`${buffer.fileName}-${index}`}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        downloadFile(buffer.url, buffer.fileName);
                                      }}
                                    >
                                        Download {getFileDescription(buffer)}
                                    </Link>
                                  ))}
                                </VStack>
                              </PopoverBody>
                            </PopoverContent>
                          </Portal>
                        </Popover>
                      </Box>
                    </VStack>
                  </Flex>
                )}
              </div>
              {longProcessing && message.type === "server" && index === messages.length - 1 && (
                <div className="spinner"></div>
              )}
            </React.Fragment>
          );
        })
      )}
    </div>
  );
}

export default MessageList;
